import { useRef, useLayoutEffect } from "react";
import { useSiteConfig, resolveImageUrl } from "../config/SiteConfigContext";
import { useCmsText } from "../hooks/useCmsText";
import { useIsMobile } from "../hooks/useIsMobile";
import { RichText } from "../components/RichText";
import { Parallax } from "react-scroll-parallax";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const FALLBACK_HERO = "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1400&h=800&fit=crop";
const FALLBACK_WHO = "https://images.unsplash.com/photo-1542838132-92c53300491e?w=800&h=1000&fit=crop";
const FALLBACK_WHAT = "https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=800&h=1000&fit=crop";
const FALLBACK_COLLECTIONS = [
  "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=400&fit=crop",
  "https://images.unsplash.com/photo-1534308983496-4fabb1a015ee?w=600&h=400&fit=crop",
  "https://images.unsplash.com/photo-1515859005217-8a1f08870f59?w=600&h=400&fit=crop",
];

export function About() {
  const cfg = useSiteConfig();
  const ct = useCmsText("about");
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(".gs-ab-hero-content > *", { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 1.3, ease: "expo.out", stagger: 0.15, delay: 0.2 });

      gsap.utils.toArray<HTMLElement>(".gs-ab-reveal").forEach((el) => {
        gsap.fromTo(el, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 1.2, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 82%", once: true } });
      });

      gsap.utils.toArray<HTMLElement>(".gs-ab-img-reveal").forEach((el) => {
        gsap.fromTo(el, { clipPath: "inset(100% 0 0 0)" }, { clipPath: "inset(0% 0 0 0)", duration: 1.4, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 82%", once: true } });
      });

      gsap.utils.toArray<HTMLElement>(".gs-ab-stagger").forEach((el) => {
        gsap.fromTo(el.children, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.9, stagger: 0.12, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 82%", once: true } });
      });

      gsap.utils.toArray<HTMLElement>(".gs-ab-line").forEach((el) => {
        gsap.fromTo(el, { width: 0 }, { width: el.dataset.w || "48", duration: 1.2, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 90%", once: true } });
      });

      gsap.utils.toArray<HTMLElement>(".gs-ab-counter").forEach((el) => {
        const target = parseInt(el.dataset.to || "0");
        gsap.fromTo(el, { textContent: "0" }, {
          textContent: target, duration: 2, ease: "power2.out", snap: { textContent: 1 },
          scrollTrigger: { trigger: el, start: "top 85%", once: true },
        });
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} style={{ overflow: "hidden" }}>

      {/* ═══ HERO — Cinematic full-bleed ═══ */}
      <section style={{ position: "relative", height: isMobile ? "70vh" : "80vh", maxHeight: 700, minHeight: isMobile ? 400 : 500, overflow: "hidden" }}>
        <Parallax speed={isMobile ? -8 : -20} style={{ position: "absolute", inset: "-25% 0", height: "150%" }}>
          <div style={{ width: "100%", height: "100%", backgroundImage: `url(${ct.image("heroImage") ? resolveImageUrl(ct.image("heroImage")!) : FALLBACK_HERO})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        </Parallax>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.72) 100%)" }} />

        <div className="gs-ab-hero-content" style={{ position: "absolute", bottom: 0, left: 0, right: 0, maxWidth: 1280, margin: "0 auto", padding: isMobile ? "0 24px 48px" : "0 48px 72px", zIndex: 2 }}>
          <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: cfg.color_accent, margin: "0 0 16px" }}>
            {cfg.brand_name}
          </p>
          <h1 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 34 : 56, fontWeight: 400, color: "#fff", margin: "0 0 16px", lineHeight: 1.05, letterSpacing: -0.5, maxWidth: isMobile ? 280 : 500 }}>
            {ct("title")}
          </h1>
          <RichText html={ct("subtitle")} style={{ fontSize: isMobile ? 14 : 16, color: "#fff", maxWidth: 420, lineHeight: 1.8 }} />
        </div>
      </section>

      {/* ═══ WHO WE ARE — Editorial two-column ═══ */}
      <section style={{ background: cfg.color_body_bg, padding: isMobile ? "80px 24px" : "140px 48px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: isMobile ? "block" : "grid", gridTemplateColumns: "1.1fr 1fr", gap: 80, alignItems: "center" }}>
          <div className="gs-ab-img-reveal" style={{ marginBottom: isMobile ? 40 : 0 }}>
            <Parallax speed={isMobile ? 0 : -5}>
              <div style={{ overflow: "hidden", lineHeight: 0, position: "relative" }}>
                <img src={ct.image("whoWeAreImage") ? resolveImageUrl(ct.image("whoWeAreImage")!) : FALLBACK_WHO} alt={ct("whoWeAre")} loading="lazy" style={{ width: "100%", height: isMobile ? 300 : 520, objectFit: "cover", display: "block" }} />
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "30%", background: "linear-gradient(transparent, rgba(0,0,0,0.15))" }} />
              </div>
            </Parallax>
          </div>
          <div className="gs-ab-reveal">
            <div className="gs-ab-line" data-w="48" style={{ height: 1, background: cfg.color_muted, marginBottom: 24 }} />
            <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: cfg.color_accent, margin: "0 0 20px" }}>
              {ct("whoWeAre")}
            </p>
            <RichText html={ct("whoWeAreText")} style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 22 : 28, fontWeight: 400, color: cfg.color_dark, lineHeight: 1.6, letterSpacing: -0.3 }} />
          </div>
        </div>
      </section>

      {/* ═══ WHAT WE DO — Reversed ═══ */}
      <section style={{ background: cfg.color_light_bg, padding: isMobile ? "80px 24px" : "140px 48px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: isMobile ? "block" : "grid", gridTemplateColumns: "1fr 1.1fr", gap: 80, alignItems: "center" }}>
          <div className="gs-ab-reveal" style={{ order: isMobile ? 2 : 1 }}>
            <div className="gs-ab-line" data-w="48" style={{ height: 1, background: cfg.color_muted, marginBottom: 24 }} />
            <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: cfg.color_accent, margin: "0 0 20px" }}>
              {ct("whatWeDo")}
            </p>
            <RichText html={ct("whatWeDoText")} style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 22 : 28, fontWeight: 400, color: cfg.color_dark, lineHeight: 1.6, letterSpacing: -0.3 }} />
          </div>
          <div className="gs-ab-img-reveal" style={{ order: isMobile ? 1 : 2, marginBottom: isMobile ? 40 : 0 }}>
            <Parallax speed={isMobile ? 0 : -5}>
              <div style={{ overflow: "hidden", lineHeight: 0, position: "relative" }}>
                <img src={ct.image("whatWeDoImage") ? resolveImageUrl(ct.image("whatWeDoImage")!) : FALLBACK_WHAT} alt={ct("whatWeDo")} loading="lazy" style={{ width: "100%", height: isMobile ? 300 : 520, objectFit: "cover", display: "block" }} />
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "30%", background: "linear-gradient(transparent, rgba(0,0,0,0.15))" }} />
              </div>
            </Parallax>
          </div>
        </div>
      </section>

      {/* ═══ STATS BAND ═══ */}
      <section style={{ background: cfg.color_dark, padding: isMobile ? "48px 24px" : "64px 48px" }}>
        <div className="gs-ab-stagger" style={{ maxWidth: 900, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: isMobile ? 32 : 0 }}>
          {[
            { value: 100, suffix: "+", label: ct("whoWeAre") === "Chi siamo" ? "Prodotti" : "Products" },
            { value: 3, suffix: "", label: ct("collectionsTitle").split(" ")[0] || "Collezioni" },
            { value: 50, suffix: "€", label: ct("whoWeAre") === "Chi siamo" ? "Soglia fiscale" : "Tax threshold" },
            { value: 15, suffix: "+", label: ct("whoWeAre") === "Chi siamo" ? "Confezioni" : "Gift boxes" },
          ].map((stat, i) => (
            <div key={i} style={{ textAlign: "center", borderRight: !isMobile && i < 3 ? "1px solid rgba(255,255,255,0.08)" : "none" }}>
              <div style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 32 : 44, fontWeight: 400, color: "#fff", lineHeight: 1, marginBottom: 6, letterSpacing: -1 }}>
                <span className="gs-ab-counter" data-to={stat.value}>0</span>{stat.suffix}
              </div>
              <p style={{ fontSize: 10, fontWeight: 500, letterSpacing: 2, textTransform: "uppercase", color: "#fff", margin: 0 }}>{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ COLLECTIONS — Magazine grid ═══ */}
      <section style={{ background: cfg.color_body_bg, padding: isMobile ? "80px 24px" : "140px 48px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div className="gs-ab-reveal" style={{ marginBottom: isMobile ? 40 : 64 }}>
            <div className="gs-ab-line" data-w="48" style={{ height: 1, background: cfg.color_muted, marginBottom: 24 }} />
            <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: cfg.color_muted, margin: "0 0 14px" }}>
              {ct("collectionsTitle")}
            </p>
            <RichText html={ct("collectionsIntro")} style={{ fontSize: isMobile ? 15 : 17, color: "rgba(28,75,59,0.86)", lineHeight: 1.8, maxWidth: 500 }} />
          </div>

          <div className="gs-ab-stagger" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: isMobile ? 24 : 24 }}>
            {(["Borghi", "Citta", "Italia"] as const).map((key, i) => (
              <div key={key} style={{ position: "relative", overflow: "hidden", cursor: "pointer", transition: "transform 0.4s" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <div style={{ position: "relative", lineHeight: 0 }}>
                  <img src={ct.image(`collectionImage${i+1}`) ? resolveImageUrl(ct.image(`collectionImage${i+1}`)!) : FALLBACK_COLLECTIONS[i]} alt="" loading="lazy" style={{ width: "100%", height: isMobile ? 200 : 260, objectFit: "cover", display: "block", transition: "transform 0.5s" }} />
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(transparent 40%, rgba(0,0,0,0.65) 100%)" }} />
                  <div style={{ position: "absolute", bottom: 16, left: 20, right: 20 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2, color: cfg.color_accent }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                </div>
                <div style={{ padding: "20px 20px 24px", background: "#fff", border: "1px solid rgba(28,75,59,0.89)", borderTop: "none" }}>
                  <p style={{ fontSize: 14, color: cfg.color_dark, lineHeight: 1.75, margin: 0 }}>
                    {ct(`collection${key}`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <RichText className="gs-ab-reveal" html={ct("collectionsOutro")} style={{ fontSize: 13, color: cfg.color_muted, lineHeight: 1.7, marginTop: 28, maxWidth: 500 }} />
        </div>
      </section>

      {/* ═══ VALUES — Dark editorial ═══ */}
      <section style={{ background: cfg.color_dark, padding: isMobile ? "80px 24px" : "120px 48px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div className="gs-ab-reveal" style={{ marginBottom: isMobile ? 48 : 72, maxWidth: 400 }}>
            <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: "#fff", margin: "0 0 14px" }}>
              {ct("valuesTitle")}
            </p>
            <div style={{ width: 40, height: 1, background: "rgba(255,255,255,0.1)" }} />
          </div>

          <div className="gs-ab-stagger" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: isMobile ? 40 : 64 }}>
            {(["quality", "tradition", "care"] as const).map((key, i) => (
              <div key={key}>
                <span style={{ fontSize: 48, fontWeight: 400, fontFamily: cfg.font_heading, color: "#fff", lineHeight: 1, display: "block", marginBottom: 20 }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 20 : 24, fontWeight: 400, color: "#fff", margin: "0 0 12px", letterSpacing: -0.3 }}>
                  {ct(`value_${key}`)}
                </h3>
                <RichText html={ct(`value_${key}_desc`)} style={{ fontSize: 14, color: "#fff", lineHeight: 1.8 }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TAX ADVANTAGE + WHY CHOOSE ═══ */}
      <section style={{ background: cfg.color_body_bg, padding: isMobile ? "80px 24px" : "140px 48px" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <div className="gs-ab-reveal" style={{ textAlign: "center", marginBottom: isMobile ? 48 : 72 }}>
            <div className="gs-ab-line" data-w="48" style={{ height: 1, background: cfg.color_muted, margin: "0 auto 24px" }} />
            <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: cfg.color_muted, margin: "0 0 16px" }}>
              {ct("taxAdvantageTitle")}
            </p>
            <RichText html={ct("taxAdvantageText")} style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 20 : 26, fontWeight: 400, color: cfg.color_dark, lineHeight: 1.5, maxWidth: 600, marginLeft: "auto", marginRight: "auto", letterSpacing: -0.3 }} />
          </div>

          <div className="gs-ab-reveal">
            <div className="gs-ab-line" data-w="48" style={{ height: 1, background: cfg.color_muted, marginBottom: 24 }} />
            <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: cfg.color_muted, margin: "0 0 28px" }}>
              {ct("whyChooseTitle")}
            </p>
            <div className="gs-ab-stagger">
              {[1, 2, 3, 4, 5].map((n) => (
                <div key={n} style={{ display: "flex", alignItems: "baseline", gap: 20, padding: "18px 0", borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                  <span style={{ fontSize: 11, fontWeight: 400, fontFamily: cfg.font_heading, color: cfg.color_accent, flexShrink: 0 }}>
                    {String(n).padStart(2, "0")}
                  </span>
                  <span style={{ fontSize: 15, color: cfg.color_dark, lineHeight: 1.6 }}>
                    {ct(`whyChoose${n}`)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
